import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BrowserProvider, ContractFactory } from "ethers";
import { useWallet } from "@/contexts/WalletContext";
import { getMyTokens } from "@/lib/myTokens";
import { verifyContract } from "@/lib/api";
import { NETWORK_CONFIG } from "@/constants/network";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Hammer, CheckCircle2, PlusCircle, ExternalLink, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { RISE_IN } from "@/lib/motion";
import { TokenLogo } from "@/components/common/TokenLogo";
import { useLogoUpload } from "@/components/common/LogoUpload";
import { LOGO_ACCEPT, validateLogoFile } from "@/lib/logos";

const STAGES = {
  checking: "Checking network…", signing: "Confirm the deployment in your wallet…", mining: "Deploying on FSC… (about 3s)",
  logo: "Sign to upload your logo…",
};

const validate = ({ name, symbol, supply }) => {
  if (!name.trim() || name.trim().length > 32) return "Name must be 1–32 characters";
  if (!/^[A-Z0-9]{2,8}$/.test(symbol)) return "Symbol must be 2–8 letters or digits";
  const n = Number(supply);
  if (!Number.isInteger(n) || n < 1 || n > 1e12) return "Supply must be a whole number from 1 to 1,000,000,000,000";
  return null;
};

const ensureFscNetwork = async (eip1193, addFSCNetwork) => {
  const chain = await eip1193.request({ method: "eth_chainId" });
  return parseInt(chain, 16) === NETWORK_CONFIG.chainId || (await addFSCNetwork());
};

const deployStarterToken = async ({ eip1193, form, address, onStage }) => {
  const artifact = await (await fetch("/contracts/StarterToken.json")).json();
  onStage("signing");
  const signer = await new BrowserProvider(eip1193).getSigner();
  const contract = await new ContractFactory(artifact.abi, artifact.bytecode, signer).deploy(form.name.trim(), form.symbol, BigInt(form.supply));
  onStage("mining");
  await contract.waitForDeployment();
  const token = {
    name: form.name.trim(), symbol: form.symbol, supply: form.supply,
    address: await contract.getAddress(), txHash: contract.deploymentTransaction().hash, owner: address.toLowerCase(),
  };
  return { token, artifact };
};

const publishSource = (token, artifact) =>
  verifyContract(token.address, { contractName: artifact.contractName, source: artifact.source })
    .then(() => toast.success("Source code verified and published"))
    .catch(() => toast.error("Automatic source verification failed. You can retry from the token page."));

const deployErrorMessage = (err) => {
  if (err?.code === "ACTION_REJECTED" || err?.code === 4001 || err?.info?.error?.code === 4001) return "Deployment was rejected in your wallet";
  if (/insufficient funds/i.test(err?.message || "")) return "Not enough FMI for gas. Claim free FMI from the faucet first.";
  const reason = err?.error?.data?.message || err?.error?.message || err?.info?.error?.data?.message
    || err?.info?.error?.message || err?.shortMessage || err?.message || "";
  return `Deployment failed: ${reason.slice(0, 160) || "unknown error"}`;
};

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
    <Input data-testid={testId} {...props}
      className="mt-1 bg-[#050505] border-[#2A2411] text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#D4AF37]/50" />
  </label>
);

const LogoField = ({ file, onChange, inputKey }) => {
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);
  return (
  <label className="block">
    <span className="text-xs text-zinc-500 uppercase tracking-wider">Logo (optional · PNG, JPG or SVG · max 512 KB)</span>
    <div className="mt-1 flex items-center gap-3 p-2 rounded-md bg-[#050505] border border-[#2A2411]">
      {preview ? <img src={preview} alt="logo preview" className="w-8 h-8 rounded-full object-cover" /> : <ImagePlus size={18} className="text-zinc-600 mx-1.5" />}
      <input key={inputKey} data-testid="starter-logo-input" type="file" accept={LOGO_ACCEPT} onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="text-xs text-zinc-400 file:mr-3 file:px-3 file:py-1 file:rounded-full file:border-0 file:bg-[#D4AF37]/15 file:text-[#D4AF37]" />
    </div>
  </label>  );
};

const DeployedToken = ({ token, onAdd }) => (
  <motion.div data-testid="starter-deploy-result" {...RISE_IN}
    className="p-4 rounded-lg bg-green-500/5 border border-green-500/20 space-y-3">
    <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
      <TokenLogo key={token.logoVersion} address={token.address} symbol={token.symbol} size="md" version={token.logoVersion} testId="starter-deploy-logo" />
      <CheckCircle2 size={16} /> {token.name} ({token.symbol}) is live on FSC
    </div>
    <div>
      <p className="text-xs text-zinc-500 mb-1">Contract address</p>
      <Link to={`/address/${token.address}`} data-testid="starter-deploy-address" className="table-link font-mono text-xs break-all">{token.address}</Link>
    </div>
    <div className="flex flex-wrap gap-2">
      <Button data-testid="starter-add-to-wallet" size="sm" variant="outline" onClick={() => onAdd(token)}
        className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F3E5AB]">
        <PlusCircle size={14} className="mr-1.5" /> Add {token.symbol} to wallet
      </Button>
      <Link to={`/tx/${token.txHash}`} data-testid="starter-deploy-tx"
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white px-3 py-2">
        <ExternalLink size={12} /> View deployment tx
      </Link>
      <Link to="/my-tokens" data-testid="starter-manage-link"
        className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:text-[#F3E5AB] px-3 py-2">
        Manage in My Tokens
      </Link>
    </div>
    <pre className="text-[11px] leading-relaxed p-3 rounded-md bg-[#050505] border border-[#2A2411] text-zinc-300 overflow-x-auto">{`const token = new ethers.Contract("${token.address}", [
  "function balanceOf(address) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
], signer);
await token.transfer(friend, ethers.parseUnits("10", 18));`}</pre>
  </motion.div>
);

export const StarterTokenDeploy = ({ onDeployed }) => {
  const { address, getProvider, addFSCNetwork, addUSDTToken, checkWalletRpc } = useWallet();
  const [staleRpc, setStaleRpc] = useState(false);
  const [form, setForm] = useState({ name: "My FSC Token", symbol: "MFT", supply: "1000000" });
  const [stage, setStage] = useState(null);
  const [error, setError] = useState("");
  const [justDeployed, setJustDeployed] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoInputKey, setLogoInputKey] = useState(0);
  const uploadLogo = useLogoUpload();
  const queryClient = useQueryClient();
  const { data: mine } = useQuery({ queryKey: ["myTokens", address?.toLowerCase()], queryFn: () => getMyTokens(address), enabled: Boolean(address) });
  const latest = justDeployed?.owner === address?.toLowerCase() ? justDeployed : mine?.deployed?.[0];

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "symbol" ? e.target.value.toUpperCase() : e.target.value }));

  const deploy = async () => {
    setError("");
    const invalid = validate(form) || validateLogoFile(logoFile);
    if (invalid) return setError(invalid);
    const eip1193 = getProvider();
    if (!address || !eip1193) return setError("Connect your wallet first");
    try {
      if (!(await ensureFscNetwork(eip1193, addFSCNetwork))) return setError("Switch your wallet to FMI Smart Chain to deploy");
      setStaleRpc(false);
      setStage("checking");
      if (!(await checkWalletRpc()).ok) {
        setStaleRpc(true);
        return setError("Your wallet can't reach the FSC network. Its saved FMI Smart Chain entry probably uses an old RPC URL.");
      }
      const { token, artifact } = await deployStarterToken({ eip1193, form, address, onStage: setStage });
      setJustDeployed(token);
      queryClient.invalidateQueries({ queryKey: ["myTokens", address.toLowerCase()] });
      publishSource(token, artifact);
      toast.success(`${token.symbol} deployed!`);
      if (logoFile) {
        setStage("logo");
        await uploadLogo(token.address, logoFile)
          .then(() => { setJustDeployed({ ...token, logoVersion: Date.now() }); setLogoFile(null); setLogoInputKey((k) => k + 1); toast.success("Logo uploaded"); })
          .catch(() => toast.error("Logo upload failed. You can add it later from My Tokens."));
      }
      onDeployed?.(token);
    } catch (err) {
      setError(deployErrorMessage(err));
    } finally {
      setStage(null);
    }
  };

  const fixNetwork = async () => {
    const eip1193 = getProvider();
    try {
      await eip1193.request({
        method: "wallet_addEthereumChain",
        params: [{ chainId: NETWORK_CONFIG.chainIdHex, chainName: NETWORK_CONFIG.chainName, nativeCurrency: NETWORK_CONFIG.nativeCurrency,
          rpcUrls: NETWORK_CONFIG.rpcUrls, blockExplorerUrls: NETWORK_CONFIG.blockExplorerUrls }],
      });
      if ((await checkWalletRpc()).ok) {
        setStaleRpc(false);
        setError("");
        toast.success("FSC network updated. You can deploy now.");
      } else {
        toast.error("Still unreachable. Update the RPC URL manually in MetaMask settings.");
      }
    } catch {
      toast.error("Network update was not approved");
    }
  };

  const addToWallet = async (token) => {
    const ok = await addUSDTToken(token.address, 18, token.symbol);
    toast[ok ? "success" : "error"](ok ? `${token.symbol} added to your wallet` : "Could not add the token to your wallet");
  };

  return (
    <div data-testid="starter-deploy" className="space-y-4">
      <div className="flex items-center gap-2">
        <Hammer size={16} className="text-[#D4AF37]" />
        <h2 className="font-display text-lg font-bold text-white">Deploy your first token</h2>
      </div>
      <p className="text-sm text-zinc-400">A standard ERC-20 contract. You sign the deployment with your wallet and own the entire supply. Gas costs a tiny amount of FMI.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Name" testId="starter-name-input" value={form.name} onChange={update("name")} maxLength={32} />
        <Field label="Symbol" testId="starter-symbol-input" value={form.symbol} onChange={update("symbol")} maxLength={8} />
        <Field label="Supply" testId="starter-supply-input" value={form.supply} onChange={update("supply")} inputMode="numeric" />
      </div>
      <LogoField file={logoFile} onChange={setLogoFile} inputKey={logoInputKey} />
      {error && <p data-testid="starter-deploy-error" className="text-sm text-red-400 break-words">{error}</p>}
      {staleRpc && (
        <div data-testid="starter-stale-rpc" className="p-3 rounded-md bg-[#D4AF37]/5 border border-[#D4AF37]/30 text-xs text-zinc-300 space-y-2">
          <p>Fix it in one click, or open MetaMask → Settings → Networks → FMI Smart Chain and set the RPC URL to:</p>
          <code className="block font-mono text-[#D4AF37] break-all">{NETWORK_CONFIG.rpcUrls[0]}</code>
          <Button data-testid="starter-fix-network-btn" size="sm" onClick={fixNetwork} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB]">Update FSC network in wallet</Button>
        </div>
      )}
      <Button data-testid="starter-deploy-btn" onClick={deploy} disabled={!!stage || !address}
        className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold h-11 rounded-md transition-transform active:scale-[0.98]">
        {stage ? <><Loader2 size={16} className="animate-spin mr-2" /> {STAGES[stage]}</> : !address ? "Connect your wallet to deploy" : <><Hammer size={16} className="mr-2" /> Deploy {form.symbol || "token"} in one click</>}
      </Button>
      {latest && <DeployedToken token={latest} onAdd={addToWallet} />}
    </div>
  );
};
